import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Drawer, Tabs, Spin, message } from "antd";
import { serverFetch } from "../../server";
import ClientAbonItemStatus from "../clients/client-abon-item-status";
import ClientAbonItemPayments from "../clients/client-abon-item-payments";
import ClientAbonItemPackets from "../clients/client-abon-item-packets";
import { isMobile } from "../../extensions/design";
import "./service-monitoring.css";

const { TabPane } = Tabs;

const WIDTH_KEY = "service-monitoring-abon-drawer-width";
const DEFAULT_WIDTH = 900;
const MIN_WIDTH = 420;

const loadWidth = () => {
  try {
    const saved = Number(localStorage.getItem(WIDTH_KEY));
    return Number.isFinite(saved) && saved >= MIN_WIDTH ? saved : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
};

const clampWidth = (value) => {
  const maxWidth = Math.floor(window.innerWidth * 0.95);
  return Math.min(maxWidth, Math.max(MIN_WIDTH, value));
};

const ServiceMonitoringAbonModal = ({ saleId, onClose }) => {
  const [activeTab, setActiveTab] = useState("2");
  const [loading, setLoading] = useState(false);
  const [abon, setAbon] = useState(null);
  const [changed, setChanged] = useState(false);
  const [width, setWidth] = useState(() => clampWidth(loadWidth()));
  const [dragging, setDragging] = useState(false);
  const [wrapperEl, setWrapperEl] = useState(null);
  const widthRef = useRef(width);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const loadAbon = () => {
    if (!saleId) return;
    setLoading(true);
    serverFetch(`sale/${saleId}`)
      .then((data) => setAbon(data))
      .catch((e) => message.error(e.userMessage || "Ошибка загрузки абонемента"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAbon();
  }, [saleId]);

  useEffect(() => {
    if (!saleId || isMobile) return undefined;
    const onWindowResize = () => setWidth((prev) => clampWidth(prev));
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [saleId]);

  const handleAbonsUpdate = (refresh) => {
    setChanged(true);
    if (refresh !== false) {
      loadAbon();
    }
  };

  const handleClose = () => {
    onClose(changed);
  };

  const onResizeStart = useCallback((e) => {
    if (isMobile) return;
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
    const startX = e.clientX;
    const startWidth = widthRef.current;

    const onMove = (ev) => {
      const next = clampWidth(startWidth + (startX - ev.clientX));
      widthRef.current = next;
      setWidth(next);
    };

    const onUp = () => {
      setDragging(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      localStorage.setItem(WIDTH_KEY, String(widthRef.current));
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, []);

  return (
    <Drawer
      open={!!saleId}
      title={`Карточка абонемента${abon?.productName ? `: ${abon.productName}` : ""} #${saleId}`}
      onClose={handleClose}
      placement="right"
      width={isMobile ? "100%" : width}
      destroyOnClose
      maskClosable
      getContainer={() => document.body}
      rootClassName={`service-monitoring-abon-drawer${dragging ? " service-monitoring-abon-drawer-dragging" : ""}`}
      styles={{ body: { paddingTop: 8 } }}
      afterOpenChange={(open) => {
        if (open && !isMobile) {
          const el = document.querySelector(
            ".service-monitoring-abon-drawer .ant-drawer-content-wrapper"
          );
          setWrapperEl(el);
        } else {
          setWrapperEl(null);
        }
      }}
    >
      {wrapperEl &&
        createPortal(
          <div
            className="service-monitoring-abon-drawer-resize"
            onMouseDown={onResizeStart}
            title="Изменить ширину"
          />,
          wrapperEl
        )}
      {loading && !abon ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : abon ? (
        <Tabs activeKey={activeTab} onChange={setActiveTab} type="line" className="no-custom-tabs">
          <TabPane tab="Статус" key="2">
            {activeTab === "2" && (
              <ClientAbonItemStatus id={saleId} onAbonsUpdate={handleAbonsUpdate} />
            )}
          </TabPane>
          <TabPane tab="Платежи" key="3">
            {activeTab === "3" && (
              <ClientAbonItemPayments id={saleId} onAbonsUpdate={handleAbonsUpdate} abon={abon} />
            )}
          </TabPane>
          <TabPane tab="Пакеты" key="4">
            {activeTab === "4" && (
              <ClientAbonItemPackets id={saleId} onAbonsUpdate={handleAbonsUpdate} />
            )}
          </TabPane>
        </Tabs>
      ) : null}
    </Drawer>
  );
};

export default ServiceMonitoringAbonModal;
